import { ref } from '@vue/composition-api';
import { useCrisisFacilityLockSocket } from '../useCrisisFacilityLockSocket';

export function useCrisisRemoteLockRefresh({
  props,
  emit,
  vm,
  activeBranches,
  acquireLock,
  applyRemoteLockAcquired,
  fetchFacilityDetails,
  getBranchKey,
  getLock,
  isBranchActive,
  requestScrollerRefresh,
  LOCK_BLOCKED,
  LOCK_OWNED,
}) {
  const branchRefreshVersions = ref({});

  // ===== Start: Socket payload matching =====
  // Locates the open facility affected by an incoming lock event.
  const isSameCrisisLockEvent = (payload) => {
    if (!payload?.id_operational_security_crisis) {
      return true;
    }

    return String(payload.id_operational_security_crisis) === String(props.operationalSecurityCrisis.id);
  };

  const findBranchByLockPayload = (payload) => {
    if (!isSameCrisisLockEvent(payload)) {
      return null;
    }

    return props.operationalSecurityCrisis.units.find(
      unit => String(unit.id_unit) === String(payload?.id_unit)
    );
  };
  // ===== End: Socket payload matching =====

  // ===== Start: Current branch resolution =====
  // Reads the latest branch object after the parent patches the unit list.
  const getCurrentBranch = (branch) => props.operationalSecurityCrisis.units.find(
    unit => String(unit.id_unit) === String(branch?.id_unit)
  ) || branch;
  // ===== End: Current branch resolution =====

  // ===== Start: Employee refresh versioning =====
  // Bumps a per-branch counter so the active Employees tab can refetch its table.
  const getBranchRefreshVersion = (branch) => branchRefreshVersions.value[getBranchKey(branch)] ?? 0;

  const bumpBranchRefreshVersion = (branch) => {
    const key = getBranchKey(branch);

    branchRefreshVersions.value = {
      ...branchRefreshVersions.value,
      [key]: getBranchRefreshVersion(branch) + 1
    };
  };
  // ===== End: Employee refresh versioning =====

  // ===== Start: Lock retry eligibility =====
  // Retries ownership only when the open branch was blocked by another user.
  const canRetryLockFromSocket = (branch) => (
    branch &&
    !props.isReadOnly &&
    props.showOptionsByStatus &&
    isBranchActive(branch) &&
    getLock(branch).status === LOCK_BLOCKED
  );

  const shouldRefreshReleasedBranch = (branch) => (
    branch &&
    isBranchActive(branch) &&
    getLock(branch).status !== LOCK_OWNED
  );
  // ===== End: Lock retry eligibility =====

  // ===== Start: Facility detail refresh =====
  // Fetches fresh detail data and patches it through the parent source of truth.
  const refreshBranchFromServer = async (branch, force = false, options = {}) => {
    const keepOpen = options.keepOpen ?? true;
    const detail = await fetchFacilityDetails(branch, force);

    if (!detail) {
      requestScrollerRefresh(true);
      return getCurrentBranch(branch);
    }

    // Parent patching keeps the unit list as the single source of truth.
    emit('build-data-from-response', { units: [detail] }, { mode: 'patch' });
    await vm.$nextTick();

    const currentBranch = getCurrentBranch(branch);
    const key = getBranchKey(currentBranch);

    if (keepOpen) {
      // Preserve the opened accordion after its branch object is replaced.
      activeBranches.value = {
        ...activeBranches.value,
        [key]: true
      };
    }

    bumpBranchRefreshVersion(currentBranch);
    requestScrollerRefresh(true);

    return currentBranch;
  };
  // ===== End: Facility detail refresh =====

  // ===== Start: Remote release handling =====
  // Refreshes released facilities and reacquires the lock when editing can resume.
  const handleRemoteLockReleased = async (payload) => {
    const branch = findBranchByLockPayload(payload);
    const shouldRetryLock = canRetryLockFromSocket(branch);

    if (!shouldRefreshReleasedBranch(branch)) {
      return;
    }

    const currentBranch = await refreshBranchFromServer(branch, true);

    if (shouldRetryLock) {
      // Reacquire after patching the latest branch data.
      await acquireLock(currentBranch, true);
    }

    requestScrollerRefresh(true);
  };
  // ===== End: Remote release handling =====

  // ===== Start: Remote acquire handling =====
  // Marks an open facility as blocked when another user acquires its lock.
  const handleRemoteLockAcquired = (payload) => {
    const branch = findBranchByLockPayload(payload);

    if (!branch || !isBranchActive(branch)) {
      return;
    }

    const currentUserId = props.user?.id;
    const acquiredByCurrentUser = payload?.acquired_by && String(payload.acquired_by) === String(currentUserId);

    if (acquiredByCurrentUser || getLock(branch).status === LOCK_OWNED) {
      return;
    }

    applyRemoteLockAcquired(branch, payload);
    requestScrollerRefresh(true);
  };
  // ===== End: Remote acquire handling =====

  // ===== Start: Socket subscription =====
  // Connects lock events to the local accordion refresh handlers.
  useCrisisFacilityLockSocket(props.operationalSecurityCrisis.id, {
    onAcquired: handleRemoteLockAcquired,
    onReleased: handleRemoteLockReleased,
  });
  // ===== End: Socket subscription =====

  return {
    getBranchRefreshVersion,
    refreshBranchFromServer,
  };
}
